import React, { useMemo, useCallback } from 'react';
import { TCurrencyCode } from 'interfaces';
import { TextInput } from 'ui/TextInput';
import { Multiselect } from 'ui/Multiselect';
import { formatPriceCents } from 'utils';
import { getCurrencySymbol } from 'utils/currency';
import { useCompaniesQuery } from '../hooks/useCompaniesQuery';

interface ICompany {
  uuid: string;
  name: string;
}

interface IVersionOption {
  value: string;
  label: string;
}

interface IDepositAccountSectionProps {
  selectedCompanyUuid: string | null;
  exchangeCurrency: TCurrencyCode;
  conversionRate: string;
  convertedAmountCents: number;
  isFetchingExchangeRate: boolean;
  onCompanyChange: (companyUuid: string | null, company: ICompany | null) => void;
  onCurrencyChange: (currency: TCurrencyCode) => void;
  onConversionRateChange: (rate: string) => void;
  btCurrency: TCurrencyCode;
  isReadOnly?: boolean;
  versionOptions?: IVersionOption[];
  selectedVersionIndex?: number;
  onVersionChange?: (index: number) => void;
}

// allowed exchange currencies for bank transaction deposits - these options are what populate the dropdown
const ALLOWED_EXCHANGE_CURRENCY_CODES = ['EUR', 'USD'] as const;

const getCurrencyOptions = (): { value: string; label: string }[] => {
  return ALLOWED_EXCHANGE_CURRENCY_CODES.map(code => ({
    value: code,
    label: code,
  }));
};

export const DepositAccountSection: React.FC<IDepositAccountSectionProps> = ({
  selectedCompanyUuid,
  exchangeCurrency,
  conversionRate,
  convertedAmountCents,
  isFetchingExchangeRate,
  onCompanyChange,
  onCurrencyChange,
  onConversionRateChange,
  btCurrency,
  isReadOnly = false,
  versionOptions,
  selectedVersionIndex,
  onVersionChange,
}) => {
  // Queries
  const { data: companies = [], isLoading: isLoadingCompanies } = useCompaniesQuery();

  // Currency options memoized
  const currencyOptions = useMemo(() => getCurrencyOptions(), []);

  // Company options for Multiselect
  const companyOptions = useMemo(() => {
    return companies.map(company => ({
      value: company.uuid,
      label: company.name,
    }));
  }, [companies]);

  // Format converted amount for display
  const formattedConvertedAmount = useMemo(() => {
    const symbol = getCurrencySymbol(exchangeCurrency);
    return `${symbol} ${formatPriceCents(convertedAmountCents)}`;
  }, [exchangeCurrency, convertedAmountCents]);

  // Format exchange rate display (1 BT_CURRENCY = X EXCHANGE_CURRENCY)
  const exchangeRateDisplay = useMemo(() => {
    const rate = parseFloat(conversionRate) || 0;
    return `1 ${btCurrency} = ${rate.toFixed(2)} ${exchangeCurrency}`;
  }, [btCurrency, exchangeCurrency, conversionRate]);

  // Get selected company name for read-only display
  const selectedCompanyName = useMemo(() => {
    if (!selectedCompanyUuid) return '';
    const company = companies.find(c => c.uuid === selectedCompanyUuid);
    return company?.name || '';
  }, [selectedCompanyUuid, companies]);

  // Handle currency change
  const handleCurrencyChange = useCallback(
    (values: string[]) => {
      if (!values[0]) return;
      onCurrencyChange(values[0] as TCurrencyCode);
    },
    [onCurrencyChange]
  );

  // Handle company change
  const handleCompanyChange = useCallback(
    (values: string[]) => {
      const companyUuid = values[0] || null;
      const company = companyUuid ? companies.find(c => c.uuid === companyUuid) || null : null;
      onCompanyChange(companyUuid, company);
    },
    [companies, onCompanyChange]
  );

  // Handle conversion rate input change
  const handleConversionRateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;
      // Allow any input while typing - validate on save
      // Only allow numeric characters and decimal point
      if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
        onConversionRateChange(value);
      }
    },
    [onConversionRateChange]
  );

  // Handle version change
  const handleVersionChange = useCallback(
    (values: string[]) => {
      const index = parseInt(values[0] || '0', 10);
      onVersionChange?.(index);
    },
    [onVersionChange]
  );

  return (
    <div className="font-pt-sans mt-30px">
      {/* Section Header - with version dropdown when processed */}
      <div className="flex items-center justify-between mb-15px">
        <span className="block font-bold text-[17px] text-black">Travel Company Account</span>

        {versionOptions && versionOptions.length > 0 && (
          <div className="w-[280px]">
            <Multiselect
              options={versionOptions}
              selectedValues={[String(selectedVersionIndex ?? 0)]}
              onUpdate={handleVersionChange}
              isSingleSelectMode={true}
              isCloseOnSelect={true}
              hideCheckboxes={true}
              className="bg-ivory [&_.multiselect-selected-label]:truncate"
              itemsClassname="bg-white"
              itemCtaClassName="hover:bg-gray-10"
            />
          </div>
        )}
      </div>

      {/* Fieldset with border - two column layout: left (fields) and right (converted amount) */}
      <div className="relative border border-gray-20 rounded bg-white p-15px flex justify-between items-stretch gap-20px">
        {/* Left - Form Fields */}
        <div className="flex gap-20px items-end">
          {/* Travel Company */}
          <div className="flex flex-col h-[57px] justify-between w-[300px]">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Travel Company *</label>
            {isReadOnly ? (
              <TextInput id="travel-company" value={selectedCompanyName} disabled />
            ) : (
              <Multiselect
                options={companyOptions}
                selectedValues={selectedCompanyUuid ? [selectedCompanyUuid] : []}
                onUpdate={handleCompanyChange}
                placeholderText={isLoadingCompanies ? 'Loading...' : 'Select company...'}
                isSingleSelectMode={true}
                isCloseOnSelect={true}
                hideCheckboxes={true}
                isEnableFuzzySearch={true}
                className="bg-ivory"
                itemsClassname="bg-white"
                itemCtaClassName="hover:bg-gray-10"
                disabled={isLoadingCompanies}
              />
            )}
          </div>

          {/* Currency Exchange */}
          <div className="flex flex-col h-[57px] justify-between w-[200px]">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Currency Exchange</label>
            {isReadOnly ? (
              <TextInput id="currency-exchange" value={exchangeCurrency} disabled />
            ) : (
              <Multiselect
                options={currencyOptions}
                selectedValues={[exchangeCurrency]}
                onUpdate={handleCurrencyChange}
                placeholderText="Select..."
                isSingleSelectMode={true}
                isCloseOnSelect={true}
                hideCheckboxes={true}
                isEnableFuzzySearch={true}
                className="bg-white"
                itemsClassname="bg-white"
                itemCtaClassName="hover:bg-gray-10"
              />
            )}
          </div>

          {/* Conversion Rate */}
          <div className="flex flex-col h-[57px] justify-between flex-1">
            <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Conversion Rate</label>
            <TextInput
              id="conversion-rate"
              value={conversionRate}
              onChange={handleConversionRateChange}
              placeholder="0.00"
              className={isFetchingExchangeRate ? 'opacity-50' : ''}
              disabled={isReadOnly}
            />
          </div>

          {/* Exchange Rate Display */}
          <div className="flex items-center h-[35px] self-end">
            <span className="text-13px text-black leading-17px">{exchangeRateDisplay}</span>
          </div>
        </div>

        {/* Right - Converted Amount */}
        <div className="flex flex-col justify-center items-end">
          <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Converted Amount</label>
          <span className="text-[19px] font-bold h-[35px] flex items-center">{formattedConvertedAmount}</span>
        </div>
      </div>
    </div>
  );
};
